import { useEffect, useState } from 'preact/hooks';
import {
  MAX_PO_LINES,
  MAX_SCHEDULE_ROWS,
  MILESTONES,
  PAYMENT_TERMS,
  PO_LINE_TYPES,
  poTotals,
  scheduleAmounts,
  scheduleFor,
  scheduleProblem,
  toMinor,
  type Milestone,
  type PaymentTerm,
  type PoDraftView,
  type PoLineType,
  type PoPageBody,
  type PoSavedBody,
} from '../../shared/po/po';
import { UNITS } from '../../shared/pr/form';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend } from '../lib/api';
import { formatDate } from '../lib/dates';

// Prepare purchase order (screen 10, P3.1). Procurement checks the lines and prices copied from
// the request and the chosen quote, picks the payment terms and writes a note for the CFO. The
// totals shown here are a preview; the broker works them out again on every save (D52).

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; data: PoPageBody };

interface LineForm {
  description: string;
  qty: string;
  unit: string;
  type: PoLineType;
  months: string;
  unitPrice: string;
}

interface Form {
  quoteRef: string;
  agreementNo: string;
  projectCode: string;
  receiver: string;
  phone: string;
  email: string;
  lines: LineForm[];
  paymentTerm: PaymentTerm;
  schedule: { milestone: Milestone; label: string; pct: string }[];
  terms: string;
  internalNote: string;
}

const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );

const major = (minor: number) => String(minor / 100);

function toForm(d: PoDraftView): Form {
  return {
    quoteRef: d.quoteRef,
    agreementNo: d.agreementNo,
    projectCode: d.projectCode,
    receiver: d.receiver,
    phone: d.phone,
    email: d.email,
    lines: d.lines.map((l) => ({
      description: l.description,
      qty: String(l.qty),
      unit: l.unit,
      type: l.type,
      months: String(l.months),
      unitPrice: major(l.unitPrice),
    })),
    paymentTerm: d.paymentTerm,
    schedule:
      d.paymentTerm === 'custom'
        ? d.schedule.map((s) => ({ milestone: s.milestone, label: s.label, pct: String(s.pct) }))
        : [],
    terms: d.terms,
    internalNote: d.internalNote,
  };
}

const n = (s: string) => {
  const v = Number(s.trim());
  return s.trim() && Number.isFinite(v) ? v : NaN;
};

function toBody(f: Form, etag: string) {
  return {
    etag,
    quoteRef: f.quoteRef,
    agreementNo: f.agreementNo,
    projectCode: f.projectCode,
    receiver: f.receiver,
    phone: f.phone,
    email: f.email,
    lines: f.lines.map((l) => ({
      description: l.description,
      qty: n(l.qty),
      unit: l.unit,
      type: l.type,
      months: l.type === 'monthly' ? n(l.months) : 1,
      unitPrice: n(l.unitPrice),
    })),
    paymentTerm: f.paymentTerm,
    schedule: f.schedule.map((s) => ({ milestone: s.milestone, label: s.label, pct: n(s.pct) })),
    terms: f.terms,
    internalNote: f.internalNote,
  };
}

export function PrepareOrderPage({ prNo }: { prNo: string }) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [form, setForm] = useState<Form | null>(null);
  const [etag, setEtag] = useState('');
  const [busy, setBusy] = useState(false);
  const [sendNow, setSendNow] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string; ref?: string } | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  useEffect(() => {
    let live = true;
    apiGet<PoPageBody>(`/api/prs/${encodeURIComponent(prNo)}/po`)
      .then((data) => {
        if (!live) return;
        setLoad({ kind: 'ok', data });
        setForm(toForm(data.draft));
        setEtag(data.etag);
      })
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [prNo, reload]);

  if (load.kind === 'loading' || (load.kind === 'ok' && !form)) {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>
          {load.code === 'forbidden'
            ? t('review.error.forbidden')
            : load.code === 'invalid_transition'
              ? t('po.error.notReady')
              : t('req.loadError')}
        </p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
      </section>
    );
  }
  if (!form) return null;

  const { data } = load;
  const edit = data.canEdit;
  const cur = data.currency;
  const set = (patch: Partial<Form>) => setForm({ ...form, ...patch });
  const setLine = (i: number, patch: Partial<LineForm>) =>
    set({ lines: form.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });

  // Preview only: the same rules the broker applies (shared/po/po.ts).
  const preview = poTotals(
    form.lines.map((l) => ({
      type: l.type,
      qty: Number.isFinite(n(l.qty)) ? n(l.qty) : 0,
      months: l.type === 'monthly' && Number.isInteger(n(l.months)) ? n(l.months) : 1,
      unitPrice: Number.isFinite(n(l.unitPrice)) ? toMinor(Math.max(0, n(l.unitPrice))) : 0,
    })),
    data.office.vatRate,
  );
  const rows = scheduleFor(
    form.paymentTerm,
    form.schedule.map((s) => ({
      milestone: s.milestone,
      label: s.label,
      pct: Number.isInteger(n(s.pct)) ? n(s.pct) : 0,
    })),
  );
  const amounts = scheduleAmounts(rows, preview.total);
  const scheduleIssue = scheduleProblem(rows);

  async function save(then?: 'submit' | 'sendFirst') {
    if (!form) return;
    setBusy(true);
    setNote(null);
    setFields({});
    const base = `/api/prs/${encodeURIComponent(prNo)}/po` as const;
    try {
      const saved = await apiSend<PoSavedBody>('PATCH', base, toBody(form, etag));
      setEtag(saved.etag);
      setForm(toForm(saved.draft));
      if (then === 'submit') {
        await apiSend('POST', `${base}/submit`, { etag: saved.etag });
        setNote({ ok: true, text: t('po.done.submitted') });
        setReload((r) => r + 1);
      } else if (then === 'sendFirst') {
        const out = await apiSend<{ poNo: string; signDue: string }>('POST', `${base}/send-first`, {
          etag: saved.etag,
        });
        setNote({
          ok: true,
          text: t('po.done.sentFirst', { no: out.poNo, date: formatDate(out.signDue, lang) }),
        });
        setSendNow(false);
        setReload((r) => r + 1);
      } else {
        setNote({ ok: true, text: t('po.done.saved') });
      }
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setFields(e?.fields ?? {});
      const known = [
        'invalid_input',
        'invalid_transition',
        'conflict',
        'not_configured',
        'data_unavailable',
        'not_eligible',
      ];
      setNote({
        ok: false,
        text: t(
          (known.includes(e?.code ?? '')
            ? `po.error.${e?.code ?? ''}`
            : 'po.error.generic') as MessageKey,
        ),
        ...(e?.correlationId ? { ref: e.correlationId } : {}),
      });
    } finally {
      setBusy(false);
    }
  }

  const textField = (
    id: keyof Form,
    label: MessageKey,
    opts: { placeholder?: MessageKey; dir?: 'ltr' | 'auto'; type?: string; max?: number } = {},
  ) => (
    <div class="field">
      <label for={`po-${id}`}>{t(label)}</label>
      <input
        id={`po-${id}`}
        type={opts.type ?? 'text'}
        dir={opts.dir ?? 'auto'}
        maxLength={opts.max ?? 60}
        placeholder={opts.placeholder ? t(opts.placeholder) : undefined}
        value={form[id] as string}
        disabled={!edit}
        aria-invalid={fields[id] ? true : undefined}
        onInput={(e) => set({ [id]: (e.target as HTMLInputElement).value })}
      />
      {fields[id] && <p class="field__error">{fields[id]}</p>}
    </div>
  );

  return (
    <div class="stack">
      <p class="caption">
        <a href="#/queue">{t('nav.queue')}</a> /{' '}
        <a href={`#/requests/${prNo}`} dir="ltr">
          {prNo}
        </a>{' '}
        / <a href={`#/requests/${prNo}/quotes`}>{t('quote.crumb')}</a> / {t('po.crumb')}
      </p>
      <div class="detail-head">
        <span class="pill pill--info">{t('po.numberLater')}</span>
        {data.rush && <span class="pill pill--warning">{t('req.rush')}</span>}
        {data.poStatus === 'pendingCFO' && (
          <span class="pill pill--success">{t('status.PendingCFO')}</span>
        )}
      </div>
      <h2 class="detail-title" dir="auto">
        {data.title}
      </h2>
      {!edit && <p class="notice notice--action">{t('po.locked')}</p>}
      {data.poNo && (
        <p class="notice notice--action">
          {t('po.issued', { no: data.poNo, date: formatDate(data.sendFirst.due, lang) })}{' '}
          <a href={`#/po/${data.poNo}`}>{t('pod.open')}</a>
        </p>
      )}
      {data.verticalRemark && edit ? (
        <p class="notice notice--action" dir="auto">
          <strong>{t('po.verticalReturned')}:</strong> {data.verticalRemark}
        </p>
      ) : (
        data.cfoRemark &&
        edit && (
          <p class="notice notice--action" dir="auto">
            <strong>{t('po.cfoReturned')}:</strong> {data.cfoRemark}
          </p>
        )
      )}
      {edit && data.status === 'PODraft' && !data.poNo && (
        <ReopenQuotes
          prNo={prNo}
          suggested={data.verticalRemark || data.cfoRemark}
          open={!!(data.verticalRemark || data.cfoRemark)}
        />
      )}
      {note && (
        <div class={note.ok ? 'form-saved' : 'form-problem'} role={note.ok ? 'status' : 'alert'}>
          <p>{note.text}</p>
          {note.ref && (
            <p class="caption" dir="ltr">
              {t('error.reference', { ref: note.ref })}
            </p>
          )}
        </div>
      )}

      <div class="po-layout">
        <div class="stack">
          <section class="card" aria-labelledby="po-head">
            <h3 id="po-head" class="visually-hidden">
              {t('po.header')}
            </h3>
            <div class="reg-grid">
              <div class="field">
                <span class="field__label">{t('po.entity')}</span>
                <p class="rfq-static">
                  {data.entity.name || data.entity.code}{' '}
                  <span class="caption">· {t('po.fromRequester')}</span>
                </p>
              </div>
              <div class="field">
                <span class="field__label">{t('inv.col.supplier')}</span>
                <p class="rfq-static">{data.supplier.name}</p>
              </div>
              <div class="field">
                <span class="field__label">{t('quote.currency')}</span>
                <p class="rfq-static" dir="ltr">
                  {cur}
                </p>
              </div>
            </div>
            <div class="reg-grid">
              {textField('quoteRef', 'quote.ref', { dir: 'ltr' })}
              {textField('agreementNo', 'po.agreementNo', { dir: 'ltr', placeholder: 'po.ifAny' })}
              {textField('projectCode', 'po.projectCode', { dir: 'ltr', placeholder: 'po.ifAny' })}
            </div>
            <div class="po-delivery">
              <p class="caption">
                <strong>{t('po.deliveryPoint')}:</strong> {data.delivery.location}
                {data.delivery.address && ` · ${data.delivery.address}`}
              </p>
              <div class="reg-grid">
                {textField('receiver', 'po.receiver', { max: 120 })}
                {textField('phone', 'po.phone', { dir: 'ltr', type: 'tel', max: 30 })}
                {textField('email', 'po.email', { dir: 'ltr', type: 'email', max: 200 })}
              </div>
            </div>
          </section>

          <section class="card" aria-labelledby="po-lines">
            <div class="card__head">
              <h3 id="po-lines" class="card__title">
                {t('po.lines')}
              </h3>
              <span class="caption">{t('po.linesHint')}</span>
            </div>
            <div class="table-wrap">
              <table class="table po-lines">
                <thead>
                  <tr>
                    <th scope="col">#</th>
                    <th scope="col">{t('pr.line.description')}</th>
                    <th scope="col">{t('po.type')}</th>
                    <th scope="col" class="num">
                      {t('pr.line.qty')}
                    </th>
                    <th scope="col" class="num">
                      {t('po.unitPrice', { currency: cur })}
                    </th>
                    <th scope="col" class="num">
                      {t('po.total')}
                    </th>
                    {edit && (
                      <th scope="col">
                        <span class="visually-hidden">{t('admin.actions')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {form.lines.map((l, i) => {
                    const no = String(i + 1);
                    return (
                      <tr key={i}>
                        <td dir="ltr">{no}</td>
                        <td>
                          <input
                            class="po-input po-input--wide"
                            dir="auto"
                            maxLength={500}
                            aria-label={t('po.aria.description', { n: no })}
                            value={l.description}
                            disabled={!edit}
                            onInput={(e) =>
                              setLine(i, { description: (e.target as HTMLInputElement).value })
                            }
                          />
                        </td>
                        <td>
                          <select
                            class="po-input"
                            aria-label={t('po.aria.type', { n: no })}
                            value={l.type}
                            disabled={!edit}
                            onChange={(e) =>
                              setLine(i, {
                                type: (e.target as HTMLSelectElement).value as PoLineType,
                              })
                            }
                          >
                            {PO_LINE_TYPES.map((ty) => (
                              <option key={ty} value={ty}>
                                {t(`po.type.${ty}` as MessageKey)}
                              </option>
                            ))}
                          </select>
                          {l.type === 'monthly' && (
                            <label class="po-months">
                              ×{' '}
                              <input
                                class="po-input po-input--num"
                                type="number"
                                min={1}
                                max={60}
                                dir="ltr"
                                value={l.months}
                                disabled={!edit}
                                onInput={(e) =>
                                  setLine(i, { months: (e.target as HTMLInputElement).value })
                                }
                              />{' '}
                              {t('po.months')}
                            </label>
                          )}
                        </td>
                        <td class="po-qty">
                          <input
                            class="po-input po-input--num"
                            type="number"
                            min={0}
                            step="any"
                            dir="ltr"
                            aria-label={t('po.aria.qty', { n: no })}
                            value={l.qty}
                            disabled={!edit}
                            onInput={(e) =>
                              setLine(i, { qty: (e.target as HTMLInputElement).value })
                            }
                          />
                          <select
                            class="po-input"
                            aria-label={t('po.aria.unit', { n: no })}
                            value={l.unit}
                            disabled={!edit}
                            onChange={(e) =>
                              setLine(i, { unit: (e.target as HTMLSelectElement).value })
                            }
                          >
                            {UNITS.map((u) => (
                              <option key={u} value={u}>
                                {t(`pr.unit.${u}` as MessageKey)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td class="num">
                          <input
                            class="po-input po-input--num"
                            type="number"
                            min={0}
                            step="0.01"
                            dir="ltr"
                            aria-label={t('po.aria.price', { n: no })}
                            value={l.unitPrice}
                            disabled={!edit}
                            onInput={(e) =>
                              setLine(i, { unitPrice: (e.target as HTMLInputElement).value })
                            }
                          />
                        </td>
                        <td class="num" dir="ltr">
                          {money(preview.lineTotals[i] ?? 0)}
                        </td>
                        {edit && (
                          <td>
                            <button
                              type="button"
                              class="button button--small"
                              disabled={form.lines.length === 1}
                              aria-label={t('po.aria.remove', { n: no })}
                              onClick={() => set({ lines: form.lines.filter((_, j) => j !== i) })}
                            >
                              {t('po.remove')}
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {fields.lines && <p class="field__error">{fields.lines}</p>}
            {edit && form.lines.length < MAX_PO_LINES && (
              <div class="actions">
                <button
                  type="button"
                  class="button button--small"
                  onClick={() =>
                    set({
                      lines: [
                        ...form.lines,
                        {
                          description: '',
                          qty: '1',
                          unit: 'piece',
                          type: 'one-off',
                          months: '1',
                          unitPrice: '',
                        },
                      ],
                    })
                  }
                >
                  {t('po.addLine')}
                </button>
                <span class="caption">{t('po.addLineHint')}</span>
              </div>
            )}
            <dl class="po-totals">
              {preview.monthly > 0 && (
                <div>
                  <dt>{t('po.monthly')}</dt>
                  <dd dir="ltr">
                    {cur} {money(preview.monthly)}
                  </dd>
                </div>
              )}
              <div>
                <dt>{t('po.subtotal')}</dt>
                <dd dir="ltr">
                  {cur} {money(preview.subtotal)}
                </dd>
              </div>
              <div>
                <dt>{t('po.vat', { rate: String(data.office.vatRate) })}</dt>
                <dd dir="ltr">
                  {cur} {money(preview.vat)}
                </dd>
              </div>
              <div class="po-totals__grand">
                <dt>{t('po.grandTotal')}</dt>
                <dd dir="ltr">
                  {cur} {money(preview.total)}
                </dd>
              </div>
            </dl>
          </section>

          <section class="card" aria-labelledby="po-terms">
            <h3 id="po-terms" class="card__title">
              {t('po.paymentTerms')}
            </h3>
            <div class="field">
              <label for="po-term">{t('po.term')}</label>
              <select
                id="po-term"
                value={form.paymentTerm}
                disabled={!edit}
                onChange={(e) => {
                  const term = (e.target as HTMLSelectElement).value as PaymentTerm;
                  set({
                    paymentTerm: term,
                    schedule:
                      term === 'custom' && form.schedule.length === 0
                        ? [
                            { milestone: 'advance', label: '', pct: '50' },
                            { milestone: 'delivery', label: '', pct: '50' },
                          ]
                        : form.schedule,
                  });
                }}
              >
                {PAYMENT_TERMS.map((p) => (
                  <option key={p} value={p}>
                    {t(`po.term.${p}` as MessageKey)}
                  </option>
                ))}
              </select>
            </div>
            {form.paymentTerm === 'custom' && (
              <ul class="po-schedule-edit">
                {form.schedule.map((s, i) => {
                  const no = String(i + 1);
                  const setRow = (patch: Partial<Form['schedule'][number]>) =>
                    set({
                      schedule: form.schedule.map((r, j) => (j === i ? { ...r, ...patch } : r)),
                    });
                  return (
                    <li key={i}>
                      <select
                        class="po-input"
                        aria-label={t('po.aria.milestone', { n: no })}
                        value={s.milestone}
                        disabled={!edit}
                        onChange={(e) =>
                          setRow({ milestone: (e.target as HTMLSelectElement).value as Milestone })
                        }
                      >
                        {MILESTONES.map((m) => (
                          <option key={m} value={m}>
                            {t(`po.milestone.${m}` as MessageKey)}
                          </option>
                        ))}
                      </select>
                      {s.milestone === 'milestone' && (
                        <input
                          class="po-input"
                          dir="auto"
                          maxLength={80}
                          aria-label={t('po.aria.milestoneName', { n: no })}
                          placeholder={t('po.milestoneName')}
                          value={s.label}
                          disabled={!edit}
                          onInput={(e) => setRow({ label: (e.target as HTMLInputElement).value })}
                        />
                      )}
                      <input
                        class="po-input po-input--num"
                        type="number"
                        min={1}
                        max={100}
                        dir="ltr"
                        aria-label={t('po.aria.pct', { n: no })}
                        value={s.pct}
                        disabled={!edit}
                        onInput={(e) => setRow({ pct: (e.target as HTMLInputElement).value })}
                      />
                      %
                      {edit && (
                        <button
                          type="button"
                          class="button button--small"
                          disabled={form.schedule.length === 1}
                          aria-label={t('po.aria.removePayment', { n: no })}
                          onClick={() => set({ schedule: form.schedule.filter((_, j) => j !== i) })}
                        >
                          {t('po.remove')}
                        </button>
                      )}
                    </li>
                  );
                })}
                {edit && form.schedule.length < MAX_SCHEDULE_ROWS && (
                  <li>
                    <button
                      type="button"
                      class="button button--small"
                      onClick={() =>
                        set({
                          schedule: [
                            ...form.schedule,
                            { milestone: 'milestone', label: '', pct: '' },
                          ],
                        })
                      }
                    >
                      {t('po.addPayment')}
                    </button>
                  </li>
                )}
              </ul>
            )}
            {(fields.schedule || (form.paymentTerm === 'custom' && scheduleIssue)) && (
              <p class="field__error">{fields.schedule ?? scheduleIssue}</p>
            )}
            <table class="table po-schedule">
              <thead>
                <tr>
                  <th scope="col">{t('po.payment')}</th>
                  <th scope="col" class="num">
                    %
                  </th>
                  <th scope="col" class="num">
                    {t('po.amount', { currency: cur })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td dir="auto">
                      {r.milestone === 'milestone' && r.label
                        ? r.label
                        : t(`po.milestone.${r.milestone}` as MessageKey)}
                    </td>
                    <td class="num" dir="ltr">
                      {r.pct}
                    </td>
                    <td class="num" dir="ltr">
                      {money(amounts[i] ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div class="field">
              <label for="po-extra">{t('po.extraTerms')}</label>
              <textarea
                id="po-extra"
                rows={3}
                dir="auto"
                maxLength={2000}
                value={form.terms}
                disabled={!edit}
                onInput={(e) => set({ terms: (e.target as HTMLTextAreaElement).value })}
              />
              <p class="caption">{t('po.extraTermsHint')}</p>
            </div>
          </section>
        </div>

        <aside class="stack">
          <section class="card po-note" aria-labelledby="po-note-title">
            <label id="po-note-title" for="po-note" class="card__title">
              {t('po.cfoNote')}
            </label>
            <textarea
              id="po-note"
              rows={6}
              dir="auto"
              maxLength={2000}
              value={form.internalNote}
              disabled={!edit}
              onInput={(e) => set({ internalNote: (e.target as HTMLTextAreaElement).value })}
            />
            <p class="caption">{t('po.cfoNoteHint')}</p>
          </section>
          <section class="card" aria-labelledby="po-attached">
            <h3 id="po-attached" class="card__title">
              {t('po.attached')}
            </h3>
            <ul class="po-attached">
              {data.quote.file && <li>{t('po.attached.quote', { name: data.supplier.name })}</li>}
              <li>{t('po.attached.comparison')}</li>
              <li>{t('po.attached.pr')}</li>
            </ul>
            {data.quote.exceptionReason && (
              <p class="caption" dir="auto">
                {t('po.exception')}: {data.quote.exceptionReason}
              </p>
            )}
            {data.quote.flagged && <p class="pill pill--warning">{t('po.priceFlag')}</p>}
          </section>
          {edit && (
            <section class="card po-sendfirst" aria-labelledby="po-sf">
              <div class="field field--check">
                <input
                  id="po-sf-check"
                  type="checkbox"
                  checked={sendNow}
                  disabled={!data.sendFirst.why || busy}
                  aria-describedby="po-sf-hint"
                  onChange={(e) => setSendNow((e.target as HTMLInputElement).checked)}
                />
                <label id="po-sf" for="po-sf-check">
                  {t('po.sendFirst.title')}
                </label>
              </div>
              <p id="po-sf-hint" class="caption">
                {data.sendFirst.why === 'rush'
                  ? t('po.sendFirst.rush', { days: String(data.sendFirst.days) })
                  : data.sendFirst.why === 'small'
                    ? t('po.sendFirst.small', {
                        limit: `${data.currency} ${data.sendFirst.limit.toLocaleString('en-US')}`,
                        days: String(data.sendFirst.days),
                      })
                    : t('po.sendFirst.no')}
              </p>
            </section>
          )}
          {edit && (
            <div class="po-actions">
              <button
                type="button"
                class="button button--primary"
                disabled={busy}
                onClick={() => void save(sendNow ? 'sendFirst' : 'submit')}
              >
                {sendNow ? t('po.sendFirst.button') : t('po.submit')}
              </button>
              <button type="button" class="button" disabled={busy} onClick={() => void save()}>
                {t('po.save')}
              </button>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

/**
 * Back to quotes from the PO draft (D112), e.g. when the CFO returned the PO asking for more
 * quotations: the request goes back to Sourcing, RFQs and quotes open again, and the draft is
 * refreshed from the quote chosen next. The broker checks the rule and keeps the reason.
 */
function ReopenQuotes({
  prNo,
  suggested,
  open,
}: {
  prNo: string;
  suggested: string;
  open: boolean;
}) {
  const { t } = useI18n();
  const [reason, setReason] = useState(suggested);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function go() {
    if (!reason.trim()) return setProblem(t('review.error.reason_required'));
    setBusy(true);
    setProblem(null);
    try {
      await apiSend('POST', `/api/prs/${encodeURIComponent(prNo)}/po/reopen`, { reason });
      location.hash = `#/requests/${prNo}/quotes`;
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setProblem(
        t('po.reopen.failed') +
          (e?.correlationId ? ` ${t('error.reference', { ref: e.correlationId })}` : ''),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <details class="card po-reopen" open={open}>
      <summary>{t('po.reopen.title')}</summary>
      <p class="caption">{t('po.reopen.hint')}</p>
      {problem && (
        <p class="form-problem" role="alert">
          {problem}
        </p>
      )}
      <div class="field">
        <label for="po-reopen-reason">{t('po.reopen.reason')}</label>
        <input
          id="po-reopen-reason"
          type="text"
          maxLength={2000}
          value={reason}
          onInput={(e) => setReason((e.target as HTMLInputElement).value)}
        />
      </div>
      <button type="button" class="button" disabled={busy} onClick={() => void go()}>
        {t('po.reopen.button')}
      </button>
    </details>
  );
}
